// T-171 (design D10, D11): the pure half of a form definition's browser side — conditions, the per-field answer order and the
// patch a server answers — so tools/ds-tests runs it without a DOM. The server reads the same conditions (FieldCondition.cs)
// over the same case table (tests/OJE.Tests/Forms/condition-cases.json).
import { latinDigits } from '../rules.ts';

/** A declared condition (FormView.FieldsJson). */
export interface Condition {
  field: string;
  op: 'equals' | 'empty' | 'not-empty';
  values?: string[];
}

/** What the page knows of one field, by its key (data-ds-fields). */
export interface FieldConfig {
  id: string;
  visible?: Condition;
  enabled?: Condition;
  required?: Condition;
  change?: { url: string; debounce: number };
  step?: number | null;
  rows?: RowsConfig | null;
}

/** One change of a FormPatch (FormPatch.cs → { patch: [...] }). */
export interface PatchOp {
  op:
    | 'set'
    | 'options'
    | 'show'
    | 'hide'
    | 'enable'
    | 'disable'
    | 'require'
    | 'optional'
    | 'error'
    | 'message'
    | 'row-add'
    | 'rows'
    | 'step';
  field?: string | null;
  value?: string | null;
  text?: string | null;
  items?: { value: string; label: string }[] | null;
  message?: string | null;
  kind?: string | null;
  rows?: unknown;
}

const OPS = new Set([
  'set',
  'options',
  'show',
  'hide',
  'enable',
  'disable',
  'require',
  'optional',
  'error',
  'message',
  'row-add',
  'rows',
  'step',
]);

/** The value as both sides compare it: trimmed, Latin digits; the legacy «undefined» / «null» are empty. */
export function normal(value: string | null | undefined): string {
  const v = latinDigits((value ?? '').trim());
  return v === 'undefined' || v === 'null' ? '' : v;
}

/** Whether a condition holds for the other field's value (FieldCondition.Holds). */
export function holds(condition: Condition, value: string | null | undefined): boolean {
  const v = normal(value);
  if (condition.op === 'empty') return v === '';
  if (condition.op === 'not-empty') return v !== '';
  return (condition.values ?? []).some(x => normal(x) === v);
}

/** The state a field's conditions give it, from the current values by key. */
export function fieldState(
  config: FieldConfig,
  valueOf: (key: string) => string | null | undefined,
): { visible: boolean | null; enabled: boolean | null; required: boolean | null } {
  return {
    visible: config.visible ? holds(config.visible, valueOf(config.visible.field)) : null,
    enabled: config.enabled ? holds(config.enabled, valueOf(config.enabled.field)) : null,
    required: config.required ? holds(config.required, valueOf(config.required.field)) : null,
  };
}

/** The patch in an answer body (`{ patch: [...] }`), only the operations the platform knows; [] when there is none. */
export function patchOf(body: unknown): PatchOp[] {
  const patch = (body as { patch?: unknown } | null)?.patch;
  if (!Array.isArray(patch)) return [];
  return patch.filter(
    (p): p is PatchOp => typeof p === 'object' && p !== null && OPS.has(String((p as { op?: unknown }).op)),
  );
}

/**
 * The order of a field's reactions: each ask takes the next number; an answer is applied only when its number is still the
 * last one asked for that field (an older answer that arrives late is dropped).
 */
export class Sequencer {
  private readonly last = new Map<string, number>();

  next(key: string): number {
    const n = (this.last.get(key) ?? 0) + 1;
    this.last.set(key, n);
    return n;
  }

  isLatest(key: string, n: number): boolean {
    return this.last.get(key) === n;
  }
}

/** A rows field as the page knows it (FormView.FieldsJson → rows). */
export interface RowsConfig {
  post: 'json' | 'indexed';
  min: number;
  max?: number | null;
  add?: string | null;
  remove?: string | null;
  removable: boolean;
  minMessage?: string | null;
  fields: {
    key: string;
    label: string;
    kind: string;
    draft: string;
    shown: boolean;
    required: boolean;
    unique?: boolean;
  }[];
}

/** One row: its posted values by row key, and the texts shown for ids (FormRow.cs). */
export interface Row {
  values: Record<string, string | null>;
  texts?: Record<string, string | null> | null;
}

/** The rows a server gave (`{ values, texts }`) or a JSON post held (plain objects), as rows. */
export function rowsFrom(raw: unknown): Row[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((r): r is Record<string, unknown> => typeof r === 'object' && r !== null)
    .map(r => {
      const values = (typeof r.values === 'object' && r.values !== null ? r.values : r) as Record<string, unknown>;
      const texts = typeof r.texts === 'object' && r.texts !== null ? (r.texts as Record<string, string | null>) : null;
      return {
        values: Object.fromEntries(
          Object.entries(values).map(([k, v]) => [k, v === null || v === undefined ? null : String(v)]),
        ),
        texts,
      };
    });
}

/**
 * What a rows field posts (design D14): one JSON array of objects under its name, or one field per cell as MVC binds a list
 * (`experts[0].personId`). Every row field is posted, the hidden and the server-given ones too.
 */
export function rowsPost(name: string, config: RowsConfig, rows: Row[]): [string, string][] {
  const keys = config.fields.map(f => f.key);
  if (config.post === 'json')
    return [[name, JSON.stringify(rows.map(r => Object.fromEntries(keys.map(k => [k, r.values[k] ?? null]))))]];
  return rows.flatMap((r, i) => keys.map(k => [`${name}[${i}].${k}`, r.values[k] ?? ''] as [string, string]));
}

/** Too few or too many rows ('min' / 'max'), else null. */
export function rowsProblem(config: RowsConfig, count: number): 'min' | 'max' | null {
  if (count < config.min) return 'min';
  if (config.max != null && count > config.max) return 'max';
  return null;
}

/** A cell as the table shows it: the server's text, else the value (a money value with separators). */
export function cellText(row: Row, key: string, kind: string): string {
  const text = row.texts?.[key];
  if (text != null) return text;
  const value = row.values[key] ?? '';
  if (kind === 'money' && /^\d+$/.test(value)) return Number(value).toLocaleString('en-US');
  return value;
}

/** A cell error's field (`experts[1].amount` → { key: 'experts', row: 1, cell: 'amount' }); null for a plain key. */
export function cellOf(field: string): { key: string; row: number; cell: string } | null {
  const m = /^([^[\]]+)\[(\d+)\]\.(.+)$/.exec(field);
  return m ? { key: m[1], row: Number(m[2]), cell: m[3] } : null;
}

/** The messages of rules.fa.json the browser shows itself (form-logic.test.mjs checks they are the catalogue's). */
export const MESSAGES = {
  required: 'این فیلد را پر کنید.',
  duplicate: 'این مقدار در ردیف دیگری هم آمده است.',
  fileType: 'نوع این فایل مجاز نیست.',
  fileTooBig: 'حجم این فایل بیشتر از حد مجاز است.',
} as const;

/** Design D16: a chosen file against the field's extensions («.pdf,.jpg») and ceiling in megabytes (FormValidator.CheckFile). */
export function fileProblem(name: string, size: number, accept: string, maxMb: number | null): 'type' | 'size' | null {
  const allowed = accept
    .split(',')
    .map(a => a.trim().toLowerCase())
    .filter(Boolean);
  const dot = name.lastIndexOf('.');
  const extension = dot < 0 ? '' : name.slice(dot).toLowerCase();
  if (allowed.length && !allowed.includes(extension)) return 'type';
  if (maxMb != null && size > maxMb * 1024 * 1024) return 'size';
  return null;
}
